"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  TextArea,
  SelectInput,
  CheckboxRow,
  GuardedPrintButton,
  CompletionPanel,
  TableScroll,
  PrintFooter,
  trackToolResult,
  EvidenceFields,
  EvidencePrint,
  evidenceRequiredItems,
  useSavedEvidence,
  type Evidence,
  type Verdict,
} from "@/components/tools/ui";

/* Turns an incident or near miss into a one-page, poster-style briefing
   bulletin. Severity drives the header band colour of the OUTPUT only — it is
   not a risk-rating of the reader's site. Mapping:
     information -> low      (emerald)
     caution     -> medium   (amber)
     danger      -> veryhigh (red)
   The output is designed to be legible pinned to a site cabin wall: large
   type, high contrast, minimal chrome. */

type Severity = "information" | "caution" | "danger";

const SEVERITIES: { value: Severity; label: string; verdict: Verdict; band: string; tint: string; kicker: string }[] = [
  {
    value: "information",
    label: "Information — for awareness",
    verdict: "low",
    band: "bg-emerald-deep text-white",
    tint: "border-emerald/40 bg-emerald-tint",
    kicker: "Safety information",
  },
  {
    value: "caution",
    label: "Caution — act this week",
    verdict: "medium",
    band: "bg-[#b8860b] text-white",
    tint: "border-[#d9a800]/40 bg-[#fdf6e3]",
    kicker: "Safety caution",
  },
  {
    value: "danger",
    label: "Danger — stop and act now",
    verdict: "veryhigh",
    band: "bg-[#a31616] text-white",
    tint: "border-[#a31616]/40 bg-[#fdecec]",
    kicker: "Safety danger",
  },
];

function severityMeta(s: Severity) {
  return SEVERITIES.find((x) => x.value === s) ?? SEVERITIES[0];
}

interface Row {
  id: number;
  text: string;
}

let nextId = 1;
function emptyRow(): Row {
  return { id: nextId++, text: "" };
}

/* A reusable add/remove bullet-list builder. */
function ListBuilder({
  label,
  hint,
  placeholder,
  addLabel,
  rows,
  setRows,
}: {
  label: string;
  hint?: string;
  placeholder: string;
  addLabel: string;
  rows: Row[];
  setRows: React.Dispatch<React.SetStateAction<Row[]>>;
}) {
  return (
    <div>
      <span className="mb-1.5 block text-[0.85rem] font-semibold text-ink">{label}</span>
      {hint && <span className="-mt-0.5 mb-2 block text-[0.78rem] text-muted">{hint}</span>}
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={row.id} className="flex items-center gap-2">
            <span className="select-none text-sm font-bold text-muted">{i + 1}.</span>
            <TextInput
              value={row.text}
              onChange={(e) =>
                setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, text: e.target.value } : r)))
              }
              placeholder={placeholder}
            />
            {rows.length > 1 && (
              <button
                type="button"
                aria-label={`Remove item ${i + 1}`}
                onClick={() => setRows((rs) => rs.filter((r) => r.id !== row.id))}
                className="shrink-0 rounded-full px-2 py-1 text-[0.8rem] font-semibold text-muted hover:text-risk-veryhigh"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setRows((rs) => [...rs, emptyRow()])}
        className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-line-2 px-3.5 py-1.5 text-[0.82rem] font-semibold text-ink hover:border-ink hover:bg-mist"
      >
        + {addLabel}
      </button>
    </div>
  );
}

const ANON_CHECKS: { id: string; label: string }[] = [
  { id: "names", label: "Names removed — no individuals identified anywhere in the alert" },
  { id: "photos", label: "Faces / identifying photos removed or blurred" },
  { id: "blame", label: "No blame language — focuses on the hazard and the fix, not who was at fault" },
];

export function SafetyAlertBuilder() {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [severity, setSeverity] = useState<Severity>("caution");
  const [whatHappened, setWhatHappened] = useState("");
  const [couldHave, setCouldHave] = useState("");
  const [actions, setActions] = useState<Row[]>([emptyRow()]);
  const [checks, setChecks] = useState<Row[]>([emptyRow()]);
  const [contact, setContact] = useState("");
  const [appliesTo, setAppliesTo] = useState("");

  // "What changes now" — the single required action with an owner and a date.
  const [changeAction, setChangeAction] = useState("");
  const [changeOwner, setChangeOwner] = useState("");
  const [changeDate, setChangeDate] = useState("");

  // Pre-print anonymisation confirmations.
  const [anon, setAnon] = useState<Record<string, boolean>>({});

  const [evidence, setEvidence, clearEvidence] = useSavedEvidence();

  const resultTracked = useRef(false);

  const meta = severityMeta(severity);

  const actionList = useMemo(() => actions.map((r) => r.text.trim()).filter(Boolean), [actions]);
  const checkList = useMemo(() => checks.map((r) => r.text.trim()).filter(Boolean), [checks]);

  // Output is "ready" once it carries enough to brief: a title and the story.
  const ready = title.trim().length > 0 && whatHappened.trim().length > 0;

  // What an alert must carry before it goes on a wall: the story, the worst
  // outcome, the one change that has to happen now, and the anonymisation
  // confirmations so it can't out an individual.
  const requiredItems: { label: string; done: boolean }[] = [
    { label: "Alert title", done: title.trim().length > 0 },
    { label: "What happened", done: whatHappened.trim().length > 0 },
    { label: "What changes now — action", done: changeAction.trim().length > 0 },
    { label: "What changes now — owner", done: changeOwner.trim().length > 0 },
    { label: "What changes now — date", done: changeDate.trim().length > 0 },
    { label: "Names removed", done: !!anon.names },
    { label: "Faces / identifying photos removed or blurred", done: !!anon.photos },
    { label: "No blame language", done: !!anon.blame },
    ...evidenceRequiredItems(evidence),
  ];
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  const prettyChangeDate = useMemo(() => {
    if (!changeDate) return "";
    const d = new Date(changeDate + "T00:00:00");
    if (isNaN(d.getTime())) return changeDate;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  }, [changeDate]);

  const prettyDate = useMemo(() => {
    const d = new Date(date + "T00:00:00");
    if (isNaN(d.getTime())) return date;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  }, [date]);

  useEffect(() => {
    if (ready && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("safety-alert", { severity });
    }
  }, [ready, severity]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_440px]">
      {/* Inputs */}
      <div className="space-y-6">
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Build your alert</h2>
        <p className="mt-1 text-sm text-muted">
          Keep it anonymised and factual — no names, no blame. The aim is to share the lesson so it doesn&apos;t
          happen to the next crew.
        </p>

        <div className="mt-5 space-y-5">
          <Field label="Alert title" hint="Short and action-oriented — say what to watch for, not who was involved.">
            <TextInput
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Mind the gap — unguarded riser opening on Level 2"
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Date of event">
              <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Field label="Severity" hint="Sets the colour of the printed banner.">
              <SelectInput value={severity} onChange={(e) => setSeverity(e.target.value as Severity)}>
                {SEVERITIES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </SelectInput>
            </Field>
          </div>

          <Field label="What happened" hint="2–3 sentences. Anonymised — no names, no blame.">
            <TextArea
              value={whatHappened}
              onChange={(e) => setWhatHappened(e.target.value)}
              placeholder="A worker stepped backwards into an unguarded riser opening. The edge protection had been removed for a delivery earlier and not reinstated. They caught themselves on the surrounding handrail."
            />
          </Field>

          <Field label="What could have happened" hint="The worst credible outcome — why this matters.">
            <TextArea
              rows={2}
              value={couldHave}
              onChange={(e) => setCouldHave(e.target.value)}
              placeholder="A fall of over 3 metres to the level below, with a serious or fatal injury."
            />
          </Field>

          <ListBuilder
            label="Do this now — immediate actions required"
            hint="The non-negotiables. One clear instruction per line."
            placeholder="e.g. Reinstate edge protection before any work near openings"
            addLabel="Add an action"
            rows={actions}
            setRows={setActions}
          />

          <ListBuilder
            label="Check your site"
            hint="Things every crew should physically check today."
            placeholder="e.g. Walk every floor edge and confirm guarding is in place and secure"
            addLabel="Add a check"
            rows={checks}
            setRows={setChecks}
          />

          <div className="rounded-xl border border-line bg-mist/60 p-4">
            <span className="block text-[0.85rem] font-semibold text-ink">What changes now</span>
            <span className="mt-0.5 block text-[0.78rem] text-muted">
              The one thing that must change as a result — printed prominently, with an owner and a date.
            </span>
            <div className="mt-3 grid gap-3">
              <Field label="Required action">
                <TextInput
                  value={changeAction}
                  onChange={(e) => setChangeAction(e.target.value)}
                  placeholder="e.g. Edge protection reinstated and signed off before any work near openings"
                />
              </Field>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Owner">
                  <TextInput value={changeOwner} onChange={(e) => setChangeOwner(e.target.value)} placeholder="Who owns it" />
                </Field>
                <Field label="By when">
                  <TextInput type="date" value={changeDate} onChange={(e) => setChangeDate(e.target.value)} />
                </Field>
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Who to contact with questions">
              <TextInput
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="e.g. Site manager, on 07xxx xxxxxx"
              />
            </Field>
            <Field label="Applies to">
              <TextInput
                value={appliesTo}
                onChange={(e) => setAppliesTo(e.target.value)}
                placeholder="e.g. All sites / All trades working at height"
              />
            </Field>
          </div>

          <div>
            <span className="block text-[0.85rem] font-semibold text-ink">Before you print — anonymisation check</span>
            <span className="-mt-0.5 mb-2 block text-[0.78rem] text-muted">
              An alert is shared widely. Confirm it can&apos;t out an individual.
            </span>
            <div className="space-y-2">
              {ANON_CHECKS.map((c) => (
                <CheckboxRow key={c.id} checked={!!anon[c.id]} onChange={(v) => setAnon((a) => ({ ...a, [c.id]: v }))}>
                  {c.label}
                </CheckboxRow>
              ))}
            </div>
          </div>
        </div>
      </div>

      <EvidenceFields value={evidence} onChange={setEvidence} onClear={clearEvidence} />
      </div>

      {/* Output: poster-style one-page alert */}
      <div className="space-y-4">
        <div className="lg:sticky lg:top-20 lg:z-10">
          <CompletionPanel items={requiredItems} title="Alert completion" />
        </div>
        {ready ? (
          <>
            <div className="print-area card overflow-hidden p-0 shadow-soft" id="safety-alert">
              {/* Header band */}
              <div className={`px-6 py-5 ${meta.band}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="font-display text-2xl font-extrabold uppercase tracking-wide">
                    ⚠ Safety alert
                  </div>
                  <div className="rounded-full border border-white/40 px-3 py-1 text-[0.72rem] font-bold uppercase tracking-wide">
                    {severity}
                  </div>
                </div>
                <h2 className="mt-2 text-2xl font-extrabold leading-tight text-white md:text-3xl">{title}</h2>
                <div className="mt-1 text-sm font-semibold text-white/85">{prettyDate}</div>
              </div>

              <div className="space-y-5 p-6">
                {appliesTo.trim() && (
                  <div className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">
                    Applies to: <span className="text-ink">{appliesTo}</span>
                  </div>
                )}

                <div>
                  <div className="text-[0.78rem] font-bold uppercase tracking-wide text-muted">What happened</div>
                  <p className="mt-1 text-[1.02rem] leading-relaxed text-ink">{whatHappened}</p>
                </div>

                {couldHave.trim() && (
                  <div>
                    <div className="text-[0.78rem] font-bold uppercase tracking-wide text-muted">
                      What could have happened
                    </div>
                    <p className="mt-1 text-[1.02rem] font-semibold leading-relaxed text-ink">{couldHave}</p>
                  </div>
                )}

                {actionList.length > 0 && (
                  <div className={`rounded-xl border-2 px-5 py-4 ${meta.tint}`}>
                    <div className="font-display text-lg font-extrabold uppercase tracking-wide text-ink">
                      Do this now
                    </div>
                    <ul className="mt-2 space-y-2">
                      {actionList.map((a, i) => (
                        <li key={i} className="flex items-start gap-2 text-[1.02rem] font-semibold leading-snug text-ink">
                          <span aria-hidden className="mt-0.5 font-extrabold">→</span>
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(changeAction.trim() || changeOwner.trim() || changeDate) && (
                  <div className="rounded-xl border-2 border-ink bg-ink px-5 py-4 text-white">
                    <div className="font-display text-lg font-extrabold uppercase tracking-wide">
                      What changes now
                    </div>
                    {changeAction.trim() && (
                      <p className="mt-1 text-[1.05rem] font-semibold leading-snug">{changeAction}</p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-[0.9rem] font-semibold">
                      {changeOwner.trim() && <span>Owner: {changeOwner}</span>}
                      {prettyChangeDate && <span>By: {prettyChangeDate}</span>}
                    </div>
                  </div>
                )}

                {checkList.length > 0 && (
                  <div>
                    <div className="font-display text-lg font-extrabold uppercase tracking-wide text-ink">
                      Check your site
                    </div>
                    <ul className="mt-2 space-y-2">
                      {checkList.map((c, i) => (
                        <li key={i} className="flex items-start gap-3 text-[1.02rem] leading-snug text-ink">
                          <span
                            aria-hidden
                            className="mt-0.5 inline-block h-5 w-5 shrink-0 rounded-[4px] border-2 border-ink"
                          />
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {contact.trim() && (
                  <div className="rounded-lg bg-mist px-4 py-3 text-[0.95rem] text-ink">
                    <span className="font-bold">Questions?</span> {contact}
                  </div>
                )}

                {/* Briefing acknowledgement sheet */}
                <div className="border-t border-line pt-4">
                  <div className="text-[0.78rem] font-bold uppercase tracking-wide text-muted">Briefing acknowledgement</div>
                  <p className="mt-1 text-[0.8rem] text-muted">
                    Everyone briefed signs to confirm they understood this alert.
                  </p>
                  <TableScroll>
                    <table className="mt-3 w-full min-w-[420px] border-collapse text-[0.8rem]">
                      <thead>
                        <tr className="text-left text-[0.72rem] uppercase tracking-wide text-muted">
                          <th className="border border-line px-2 py-1.5 font-semibold">Name</th>
                          <th className="border border-line px-2 py-1.5 font-semibold">Signature</th>
                          <th className="border border-line px-2 py-1.5 font-semibold">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: 8 }).map((_, i) => (
                          <tr key={i}>
                            <td className="border border-line px-2 py-3" />
                            <td className="border border-line px-2 py-3" />
                            <td className="border border-line px-2 py-3" />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                  <p className="mt-2 text-[0.78rem] leading-relaxed text-muted">
                    Brief this face-to-face at the workface, not by email alone. Ask the crew to explain the actions
                    back to confirm understanding, then keep the signed sheet with this alert.
                  </p>
                </div>

                <EvidencePrint value={evidence} briefing={false} />

                <PrintFooter toolName="safety alert builder" />
              </div>
            </div>

            <GuardedPrintButton
              tool="safety-alert"
              label="Print / save alert as PDF"
              missing={missingRequired}
              warningTitle="This safety alert still has gaps — check before it goes on a wall"
            />
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Add an alert title and a short, anonymised description of what happened to build your one-page
            briefing bulletin. Set the severity to colour the banner.
          </div>
        )}
      </div>
    </div>
  );
}
